import { ApiError, fillPath, getJson, sendForm, sendJson } from '@/core/api/http'

export type VerbisLebenslaufArt = 'verbis' | 'lebenslauf'

export type VerbisLebenslaufZuordnung = {
  datei: string
  art: VerbisLebenslaufArt
}

export type VerbisLebenslaufHinweis = VerbisLebenslaufFehlerZeile

export type VerbisLebenslaufUploadResult = {
  verbis: { gelesen: number; von: number; dateien: number } | null
  lebenslauf: { eintraege: number; seiten: number; hinweise: VerbisLebenslaufHinweis[] } | null
  zuordnung: VerbisLebenslaufZuordnung[]
  weiter: boolean
}

export type LebenslaufEintrag = {
  von: string | null
  bis: string | null
  eintragsart: string | null
  taetigkeit: string | null
  einrichtung: string | null
  hinweis: string | null
}

export type VerbisLebenslaufStatus = {
  verbis: { ausgelesen_am: string; felder: Record<string, unknown> } | null
  lebenslauf: { ausgelesen_am: string; eintraege: LebenslaufEintrag[]; ohne_datum: number[] } | null
}

export const VERBIS_LEBENSLAUF_ACCEPT: readonly string[] = ['image/png', 'image/jpeg']

export const VERBIS_LEBENSLAUF_MAX_DATEIEN = 24
export const VERBIS_LEBENSLAUF_MAX_BYTES_PRO_DATEI = 10 * 1024 * 1024
export const VERBIS_LEBENSLAUF_MAX_BYTES_GESAMT = 30 * 1024 * 1024

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object'
}

function isZuordnung(item: unknown): item is VerbisLebenslaufZuordnung {
  if (!isRecord(item)) return false
  return typeof item.datei === 'string' && (item.art === 'verbis' || item.art === 'lebenslauf')
}

function isVerbisHaelfte(value: unknown): value is VerbisLebenslaufUploadResult['verbis'] {
  if (value === null) return true
  if (!isRecord(value)) return false
  const { gelesen, von, dateien } = value
  return typeof gelesen === 'number' && typeof von === 'number' && typeof dateien === 'number'
}

function isHinweise(value: unknown): value is VerbisLebenslaufHinweis[] {
  return Array.isArray(value) && value.every(isVerbisLebenslaufFehlerZeile)
}

function isLebenslaufHaelfte(value: unknown): value is VerbisLebenslaufUploadResult['lebenslauf'] {
  if (value === null) return true
  if (!isRecord(value)) return false
  const { eintraege, seiten, hinweise } = value
  return typeof eintraege === 'number' && typeof seiten === 'number' && isHinweise(hinweise)
}

export function isVerbisLebenslaufUploadResult(body: unknown): body is VerbisLebenslaufUploadResult {
  if (!isRecord(body)) return false
  const { verbis, lebenslauf, zuordnung, weiter } = body
  return (
    isVerbisHaelfte(verbis) &&
    isLebenslaufHaelfte(lebenslauf) &&
    (verbis !== null || lebenslauf !== null) &&
    Array.isArray(zuordnung) &&
    zuordnung.every(isZuordnung) &&
    typeof weiter === 'boolean'
  )
}

function isLebenslaufEintrag(item: unknown): item is LebenslaufEintrag {
  if (!isRecord(item)) return false
  return (['von', 'bis', 'eintragsart', 'taetigkeit', 'einrichtung', 'hinweis'] as const).every(
    (key) => item[key] === null || typeof item[key] === 'string',
  )
}

function isVerbisPart(value: unknown): value is VerbisLebenslaufStatus['verbis'] {
  if (value === null) return true
  if (!isRecord(value)) return false
  const { ausgelesen_am, felder } = value
  return typeof ausgelesen_am === 'string' && isRecord(felder)
}

export function lebenslaufZeilenOffen(ohneDatum: readonly unknown[]): boolean {
  return ohneDatum.length > 0
}

export function isOhneDatum(value: unknown, eintraege: readonly unknown[]): value is number[] {
  return (
    Array.isArray(value) &&
    value.every((position) => Number.isInteger(position) && position >= 0 && position < eintraege.length)
  )
}

function isLebenslaufPart(value: unknown): value is VerbisLebenslaufStatus['lebenslauf'] {
  if (value === null) return true
  if (!isRecord(value)) return false
  const { ausgelesen_am, eintraege, ohne_datum } = value
  return (
    typeof ausgelesen_am === 'string' &&
    Array.isArray(eintraege) &&
    eintraege.every(isLebenslaufEintrag) &&
    isOhneDatum(ohne_datum, eintraege)
  )
}

export function isVerbisLebenslaufStatus(body: unknown): body is VerbisLebenslaufStatus {
  if (!isRecord(body)) return false
  return isVerbisPart(body.verbis) && isLebenslaufPart(body.lebenslauf)
}

export type LebenslaufZeitraumErgebnis = {
  eintrag: LebenslaufEintrag
  ohne_datum: number[]
  weiter: boolean
}

function isLebenslaufZeitraumErgebnis(body: unknown): body is LebenslaufZeitraumErgebnis {
  if (!isRecord(body)) return false
  const { eintrag, ohne_datum, weiter } = body
  return (
    typeof weiter === 'boolean' &&
    isLebenslaufEintrag(eintrag) &&
    Array.isArray(ohne_datum) &&
    ohne_datum.every((position) => Number.isInteger(position) && position >= 0)
  )
}

export type VerbisLebenslaufFehlerZeile = {
  datei: string | null
  seite: number | null
  zeile: number | null
  feld: string | null
  grund: string
}

export type VerbisLebenslaufErrorBody = {
  meldung: string
  fehler: VerbisLebenslaufFehlerZeile[]
  zuordnung: VerbisLebenslaufZuordnung[]
}

function isVerbisLebenslaufFehlerZeile(item: unknown): item is VerbisLebenslaufFehlerZeile {
  if (!isRecord(item)) return false
  const { datei, seite, zeile, feld, grund } = item
  return (
    (datei === null || typeof datei === 'string') &&
    (seite === null || typeof seite === 'number') &&
    (zeile === null || typeof zeile === 'number') &&
    (feld === null || typeof feld === 'string') &&
    typeof grund === 'string'
  )
}

export function verbisLebenslaufErrorBody(error: unknown): VerbisLebenslaufErrorBody | null {
  if (!(error instanceof ApiError)) return null
  const body = error.detail
  if (!isRecord(body) || !('detail' in body)) return null
  const inner = body.detail
  if (!isRecord(inner)) return null
  const { meldung, fehler, zuordnung = [] } = inner
  if (typeof meldung !== 'string' || !Array.isArray(fehler)) return null
  if (!fehler.every(isVerbisLebenslaufFehlerZeile)) return null
  if (!Array.isArray(zuordnung) || !zuordnung.every(isZuordnung)) return null
  return { meldung, fehler, zuordnung }
}

const MALFORMED_RESPONSE = "Die Antwort des Servers hatte nicht die erwartete Form."

export async function uploadVerbisLebenslauf(
  coachingId: string,
  files: File[],
): Promise<VerbisLebenslaufUploadResult> {
  const form = new FormData()
  for (const file of files) form.append('dateien', file, file.name)
  const body = await sendForm<unknown>(
    fillPath('/verbis-lebenslauf/{coaching_id}/dateien', { coaching_id: coachingId }),
    form,
  )
  if (!isVerbisLebenslaufUploadResult(body) || body.zuordnung.length !== files.length) {
    throw new Error(MALFORMED_RESPONSE)
  }
  return body
}

export async function getVerbisLebenslaufStatus(coachingId: string): Promise<VerbisLebenslaufStatus> {
  const body = await getJson<unknown>(
    fillPath('/verbis-lebenslauf/{coaching_id}', { coaching_id: coachingId }),
  )
  if (!isVerbisLebenslaufStatus(body)) throw new Error(MALFORMED_RESPONSE)
  return body
}

function tagMonatJahr(iso: string): string {
  const [jahr, monat, tag] = iso.split('-')
  return `${tag}.${monat}.${jahr}`
}

export async function setLebenslaufZeitraum(
  coachingId: string,
  eintrag: number,
  zeitraum: { von: string; bis: string | null },
): Promise<LebenslaufZeitraumErgebnis> {
  const body = await sendJson<unknown>(
    fillPath('/verbis-lebenslauf/{coaching_id}/lebenslauf/{eintrag}/zeitraum', {
      coaching_id: coachingId,
      eintrag: String(eintrag),
    }),
    'PUT',
    { von: tagMonatJahr(zeitraum.von), bis: zeitraum.bis === null ? null : tagMonatJahr(zeitraum.bis) },
  )
  if (!isLebenslaufZeitraumErgebnis(body)) throw new Error(MALFORMED_RESPONSE)
  return body
}

export async function verbisUeberspringen(coachingId: string): Promise<void> {
  await sendJson<unknown>(fillPath('/verbis-lebenslauf/{coaching_id}/ueberspringen', { coaching_id: coachingId }), 'POST', {})
}
